/**
 * Pure machine logic for the Roost wallboard (WI 34272): seats per host and
 * the memory/disk tiles for machines running 5+ seats.
 */
import type { ContextSnapshot } from '../models/flock-telemetry.models';
import { parseUtcMs } from '../models/flock-telemetry.utils';

// ── Thresholds (Tim, WI 34272) ──────────────────────────────────────────────

/** "The key machines running 5+ seats." */
export const MACHINE_SEAT_MIN = 5;
/** Memory or disk at or above this reads as critical on a machine tile. */
export const MACHINE_CRITICAL_PCT = 90;
/** Memory or disk at or above this reads as a warning on a machine tile. */
export const MACHINE_WARN_PCT = 80;
/** A context snapshot older than this no longer counts as a running seat. */
export const SEAT_FRESH_MS = 15 * 60_000;
/** Host samples inside this window are merged (worst disk wins). */
export const HOST_SAMPLE_WINDOW_MS = 60_000;

// ── Machines ────────────────────────────────────────────────────────────────

/** "rynais-Mac-Studio.local" and "RYNAIS-MAC-STUDIO" name the same machine. */
export function normalizeHost(host: string | null | undefined): string {
  if (!host) return '';
  return host.trim().toLowerCase().split('.')[0];
}

/**
 * Seat placement per machine, from context snapshots. `freshSeats` counts only
 * seats observed within SEAT_FRESH_MS of `nowMs` and gates the normal 5+ seat
 * rule. `allSeats` and `lastObservedAtMs` are the last-known placement: they
 * never silently read as zero just because the render clock walked past the
 * freshness window, so a machine that is still reporting live hostMetrics can
 * keep its tile with a placement explicitly marked stale (WI 34272 review).
 */
export interface HostSeatInfo {
  freshSeats: string[];
  allSeats: string[];
  lastObservedAtMs: number;
}

export function seatsByHost(
  snapshots: ContextSnapshot[],
  nowMs: number
): Map<string, HostSeatInfo> {
  const map = new Map<string, HostSeatInfo>();
  for (const s of snapshots) {
    const host = normalizeHost(s.reportingHost);
    if (!host) continue;
    if (s.processState === 'reaped' || s.processState === 'orphaned') continue;
    const observed = parseUtcMs(s.observedAt);
    if (observed === null) continue;
    const info = map.get(host) ?? {
      freshSeats: [],
      allSeats: [],
      lastObservedAtMs: 0,
    };
    if (!info.allSeats.includes(s.instanceId)) info.allSeats.push(s.instanceId);
    if (
      nowMs - observed <= SEAT_FRESH_MS &&
      !info.freshSeats.includes(s.instanceId)
    ) {
      info.freshSeats.push(s.instanceId);
    }
    if (observed > info.lastObservedAtMs) info.lastObservedAtMs = observed;
    map.set(host, info);
  }
  return map;
}

/** Minimal slice of the hostMetrics sample the wallboard needs. */
export interface HostSample {
  hostId: string;
  hostname: string;
  atMs: number;
  memPercent: number;
  memTotalBytes: number;
  diskPercent: number;
  diskTotalBytes: number;
}

export type MachineSeverity = 'ok' | 'warn' | 'critical' | 'unknown';

export interface MachineTile {
  host: string;
  seatCount: number;
  /** True when seatCount comes from last-known placement rather than a
   * currently-fresh context snapshot; the tile must say so, never pretend the
   * count is live. */
  seatsStale: boolean;
  /** Freshest context-snapshot observation for this host's placement, or null
   * when no placement was ever seen (a live-hostMetrics-only tile). */
  seatsLastObservedAtMs: number | null;
  /** Null when no hostMetrics sample has arrived for this machine. */
  memPercent: number | null;
  diskPercent: number | null;
  memTotalBytes: number | null;
  diskTotalBytes: number | null;
  lastSampleAtMs: number | null;
  /** True when no sample arrived inside the offline window. */
  offline: boolean;
  severity: MachineSeverity;
}

export const HOST_OFFLINE_MS = 30_000;

/**
 * Machines running 5+ seats with their memory and storage. Memory is the
 * newest sample. Disk is the WORST reading in the last minute: two publishers
 * currently report different volumes for one host (WI 34279), and the
 * near-full one must not flicker out of sight. Worst machine first.
 *
 * Tile inclusion is driven by LIVE hostMetrics, not solely by the
 * once-per-load context snapshot (WI 34272 review, P1): a host qualifies
 * either on a currently-fresh seat count, or on its last-known seat count
 * plus a live hostMetrics sample, or unconditionally when a live sample reads
 * critical. A machine must never silently drop off the board just because the
 * render clock walked past SEAT_FRESH_MS while it keeps reporting metrics.
 */
export function buildMachineTiles(
  seatMap: Map<string, HostSeatInfo>,
  samples: HostSample[],
  nowMs: number
): MachineTile[] {
  const byHost = new Map<string, HostSample[]>();
  for (const s of samples) {
    const key = normalizeHost(s.hostname) || normalizeHost(s.hostId);
    if (!key) continue;
    const list = byHost.get(key) ?? [];
    list.push(s);
    byHost.set(key, list);
  }

  const hosts = new Set<string>([...seatMap.keys(), ...byHost.keys()]);
  const tiles: MachineTile[] = [];
  for (const host of hosts) {
    const info = seatMap.get(host);
    const freshCount = info?.freshSeats.length ?? 0;
    const knownCount = info?.allSeats.length ?? 0;
    const seatsStale = !info || nowMs - info.lastObservedAtMs > SEAT_FRESH_MS;
    const seatsLastObservedAtMs = info?.lastObservedAtMs ?? null;
    const seatCount = freshCount >= MACHINE_SEAT_MIN ? freshCount : knownCount;

    const hostSamples = (byHost.get(host) ?? []).sort(
      (a, b) => b.atMs - a.atMs
    );
    const newest = hostSamples[0];
    const liveSample =
      newest !== undefined && nowMs - newest.atMs <= HOST_OFFLINE_MS;

    let severity: MachineSeverity = 'unknown';
    let worstDisk: HostSample | undefined;
    if (newest) {
      const recent = hostSamples.filter(
        (s) => newest.atMs - s.atMs <= HOST_SAMPLE_WINDOW_MS
      );
      worstDisk = recent.reduce((w, s) =>
        s.diskPercent > w.diskPercent ? s : w
      );
      severity = machineSeverity(newest.memPercent, worstDisk.diskPercent);
    }

    const qualifies =
      freshCount >= MACHINE_SEAT_MIN ||
      (liveSample && knownCount >= MACHINE_SEAT_MIN) ||
      (liveSample && severity === 'critical');
    if (!qualifies) continue;

    if (!newest) {
      tiles.push({
        host,
        seatCount,
        seatsStale,
        seatsLastObservedAtMs,
        memPercent: null,
        diskPercent: null,
        memTotalBytes: null,
        diskTotalBytes: null,
        lastSampleAtMs: null,
        offline: true,
        severity: 'unknown',
      });
      continue;
    }
    const offline = nowMs - newest.atMs > HOST_OFFLINE_MS;
    tiles.push({
      host,
      seatCount,
      seatsStale,
      seatsLastObservedAtMs,
      memPercent: newest.memPercent,
      diskPercent: worstDisk!.diskPercent,
      memTotalBytes: newest.memTotalBytes,
      diskTotalBytes: worstDisk!.diskTotalBytes,
      lastSampleAtMs: newest.atMs,
      offline,
      severity,
    });
  }
  return tiles.sort(compareMachines);
}

export function machineSeverity(mem: number, disk: number): MachineSeverity {
  const worst = Math.max(mem, disk);
  if (worst >= MACHINE_CRITICAL_PCT) return 'critical';
  if (worst >= MACHINE_WARN_PCT) return 'warn';
  return 'ok';
}

const SEVERITY_RANK: Record<MachineSeverity, number> = {
  critical: 0,
  warn: 1,
  unknown: 2,
  ok: 3,
};

function compareMachines(a: MachineTile, b: MachineTile): number {
  return (
    SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity] ||
    Math.max(b.memPercent ?? 0, b.diskPercent ?? 0) -
      Math.max(a.memPercent ?? 0, a.diskPercent ?? 0) ||
    b.seatCount - a.seatCount ||
    a.host.localeCompare(b.host)
  );
}
